import type { StatusDotAnimation, StatusDotColor, StatusDotVariant } from "@/components/flexkit/status-dot/StatusDot";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { type EffectiveState, effectiveState } from "@/routes/environments/phase";
import { agentExecutionState, AgentExecutionState } from "@/utils/agent-executions";
import { gitStatusChangesCount } from "@/utils/git-status";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";

export type ConversationDotProps = {
    color: StatusDotColor;
    variant: StatusDotVariant;
    animation: StatusDotAnimation;
    tooltip: string;
};

// ── Resolved conversation state ─────────────────────────────────────────

type AgentConversationState = {
    kind: "working" | "waiting" | "failed" | "stopping" | "starting" | "review" | "running" | "idle" | "stopped";
};

/**
 * When superOna is enabled, an agent waiting on sub-agents or timers (without
 * also waiting for user input) is still actively working — show it as "working"
 * rather than "waiting". A userMessage interest means the agent needs the user,
 * even if there's also a timeout timer.
 */
export function isAutonomousWait(agentExecution: PlainAgentExecution): boolean {
    const interests = agentExecution.status?.waitingInfo?.interests;
    if (!interests || interests.length === 0) {
        return false;
    }
    const hasUserMessage = interests.some((i) => i.interest.case === "userMessage");
    if (hasUserMessage) {
        return false;
    }
    return interests.some(
        (i) => i.interest.case === "subAgent" || i.interest.case === "timer" || i.interest.case === "environment",
    );
}

function resolveAgentConversationState(
    env: PlainEnvironment,
    agentExecution: PlainAgentExecution,
    opts?: { superOna?: boolean },
): AgentConversationState {
    const state = agentExecutionState(agentExecution);
    const envState = effectiveState(env);
    const envRunning = envState.state === EnvironmentPhase.RUNNING;
    const hasChanges = (gitStatusChangesCount(env.status?.content?.git) ?? 0) > 0;

    // Active agent states take priority regardless of environment phase
    switch (state) {
        case AgentExecutionState.WORKING:
        case AgentExecutionState.PENDING:
            return { kind: "working" };
        case AgentExecutionState.WAITING_FOR_INPUT:
            if (opts?.superOna && isAutonomousWait(agentExecution)) {
                return { kind: "working" };
            }
            return { kind: "waiting" };
        case AgentExecutionState.FAILED:
            return { kind: "failed" };
        case AgentExecutionState.STOPPING:
            return { kind: "stopping" };
    }

    // Environment is starting/resuming — show heartbeat regardless of agent state
    const envStarting =
        envState.state === EnvironmentPhase.STARTING ||
        envState.state === EnvironmentPhase.CREATING ||
        envState.state === EnvironmentPhase.UPDATING;
    if (envStarting) {
        return { kind: "starting" };
    }

    // Environment is stopping/deleting — show fade regardless of agent state
    const envStopping = envState.state === EnvironmentPhase.STOPPING || envState.state === EnvironmentPhase.DELETING;
    if (envStopping) {
        return { kind: "stopping" };
    }

    // Terminal agent states: green only when the environment is online.
    // A stopped environment always shows gray, even with reviewable changes.
    if (!envRunning) {
        return state === AgentExecutionState.STOPPED_BY_USER ? { kind: "stopped" } : { kind: "idle" };
    }

    switch (state) {
        case AgentExecutionState.READY_FOR_REVIEW:
            return hasChanges ? { kind: "review" } : { kind: "running" };
        case AgentExecutionState.STOPPED_BY_USER:
            return { kind: "running" };
        default:
            return { kind: "running" };
    }
}

const AGENT_DOT: Record<AgentConversationState["kind"], ConversationDotProps> = {
    working: { color: "brand", variant: "solid", animation: "spin", tooltip: "Working" },
    waiting: { color: "orange", variant: "solid", animation: "ping", tooltip: "Needs attention" },
    failed: { color: "red", variant: "solid", animation: "none", tooltip: "Failed" },
    stopping: { color: "orange", variant: "solid", animation: "spin-reverse", tooltip: "Stopping" },
    starting: { color: "brand", variant: "solid", animation: "heartbeat", tooltip: "Starting..." },
    review: { color: "green", variant: "solid", animation: "pulse", tooltip: "Ready for review" },
    running: { color: "green", variant: "solid", animation: "none", tooltip: "Running" },
    idle: { color: "gray", variant: "small", animation: "none", tooltip: "Idle" },
    stopped: { color: "gray", variant: "small", animation: "none", tooltip: "Stopped" },
};

// ── Environment-only state ──────────────────────────────────────────────

function envDotFromState(state: EffectiveState): ConversationDotProps {
    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            return { color: "green", variant: "solid", animation: "none", tooltip: "Running" };
        case EnvironmentPhase.UPDATING:
        case EnvironmentPhase.STARTING:
        case EnvironmentPhase.CREATING:
            return { color: "brand", variant: "solid", animation: "heartbeat", tooltip: "Starting..." };
        case EnvironmentPhase.STOPPING:
        case EnvironmentPhase.DELETING:
            return { color: "orange", variant: "solid", animation: "spin-reverse", tooltip: "Stopping" };
        case EnvironmentPhase.STOPPED:
            return state.failures
                ? { color: "red", variant: "solid", animation: "none", tooltip: "Failed to start" }
                : { color: "gray", variant: "small", animation: "none", tooltip: "Stopped" };
        default:
            return { color: "gray", variant: "small", animation: "none", tooltip: "Offline" };
    }
}

function envLabelFromState(state: EffectiveState): string {
    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            return "Ready";
        case EnvironmentPhase.STARTING:
        case EnvironmentPhase.CREATING:
            return "Starting...";
        case EnvironmentPhase.UPDATING:
            return "Updating...";
        case EnvironmentPhase.STOPPING:
        case EnvironmentPhase.DELETING:
            return "Stopping...";
        case EnvironmentPhase.STOPPED:
            return state.failures ? "Failed to start" : "Stopped";
        default:
            return "Offline";
    }
}

// ── Wait info summary ───────────────────────────────────────────────────

const smallNumbers = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"] as const;

/** Returns the English word for n < 10, otherwise the digit string. */
function numberWord(n: number): string {
    return n >= 0 && n < smallNumbers.length ? smallNumbers[n] : String(n);
}

/**
 * Extracts the original duration string from a timer interest (e.g. "5m").
 */
function timerLabel(timer: { duration?: string }): string {
    return timer.duration || "timer";
}

/**
 * Returns a label for the primary (non-timer) interest, or undefined if none.
 */
function primaryInterestLabel(
    interests: NonNullable<NonNullable<PlainAgentExecution["status"]>["waitingInfo"]>["interests"],
): string | undefined {
    const subAgentCount = interests.filter((i) => i.interest.case === "subAgent").length;
    if (subAgentCount > 0) {
        return subAgentCount === 1 ? "Waiting on one sub-agent" : `Waiting on ${numberWord(subAgentCount)} sub-agents`;
    }
    if (interests.some((i) => i.interest.case === "userMessage")) {
        return "Waiting for your input";
    }
    const envInterest = interests.find((i) => i.interest.case === "environment");
    if (envInterest?.interest.case === "environment") {
        const phase = envInterest.interest.value.phase;
        return phase ? `Waiting for environment to be ${phase}` : "Waiting for environment";
    }
    return undefined;
}

/**
 * Produces a compact human-readable summary of what the agent is waiting for.
 * If there's a non-timer interest, show that. If there's also a timer, append
 * the remaining time. If there's only a timer, show the time alone.
 * Examples: "Waiting on 2 sub-agents · 3m", "Waiting for your input · 2m", "Waiting for 5m"
 */
export function summarizeWaitingInfo(agentExecution: PlainAgentExecution): string | undefined {
    const interests = agentExecution.status?.waitingInfo?.interests;
    if (!interests || interests.length === 0) {
        return undefined;
    }

    const timerInterest = interests.find((i) => i.interest.case === "timer");
    const timeSuffix = timerInterest?.interest.case === "timer" ? timerLabel(timerInterest.interest.value) : undefined;

    const primary = primaryInterestLabel(interests);

    if (primary) {
        return timeSuffix ? `${primary} · ${timeSuffix}` : primary;
    }

    if (timeSuffix) {
        return `Waiting for ${timeSuffix}`;
    }

    return undefined;
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Derives StatusDot props from combined environment + agent execution state.
 */
export function getConversationDotProps(
    env: PlainEnvironment,
    agentExecution?: PlainAgentExecution,
    opts?: { superOna?: boolean },
): ConversationDotProps {
    if (agentExecution) {
        const resolved = resolveAgentConversationState(env, agentExecution, opts);
        return AGENT_DOT[resolved.kind];
    }
    return envDotFromState(effectiveState(env));
}

/**
 * Derives StatusDot props from the environment phase only, ignoring any agent
 * activity. Used by the Environment tab indicator so its dot never conflates
 * agent state (working/waiting/review) with environment state (running,
 * starting, stopping, failed). See CORE-8507.
 */
export function getEnvironmentDotProps(env: PlainEnvironment): ConversationDotProps {
    return envDotFromState(effectiveState(env));
}

/**
 * Returns a human-readable phase label for environment-only items.
 */
export function getEnvironmentLabel(env: PlainEnvironment): string {
    return envLabelFromState(effectiveState(env));
}
